import { Controller } from "@hotwired/stimulus";

// NavController drives the off-canvas sidebar on narrow viewports.
//
// Above the md breakpoint the sidebar is a normal sticky column and
// this controller does nothing — the CSS in base.html decides which
// mode is in play, so there is no viewport measuring here and no
// resize listener to keep in sync with the media query.
//
// Closing is deliberately handled in three places because a drawer
// that can only be closed by the button it was opened with is a trap
// on a phone: the scrim, Escape, and any click on a link inside the
// panel all dismiss it. Turbo navigation needs no handling at all —
// it replaces <body>, which disconnects this controller and drops the
// open state with it.
export default class NavController extends Controller {
  static targets = ["panel", "scrim"];

  connect() {
    this.onKeyDown = (e) => {
      if (e.key === "Escape") this.close();
    };
    document.addEventListener("keydown", this.onKeyDown);
  }

  disconnect() {
    document.removeEventListener("keydown", this.onKeyDown);
    // Never leave the scroll lock behind on a page that is going away.
    document.body.style.removeProperty("overflow");
  }

  toggle() {
    this.isOpen ? this.close() : this.open();
  }

  open() {
    this.panelTarget.classList.add("is-open");
    this.scrimTarget.hidden = false;
    // Stop the page behind the drawer from scrolling under a swipe.
    document.body.style.overflow = "hidden";
    this.setExpanded(true);
  }

  close() {
    if (!this.isOpen) return;
    this.panelTarget.classList.remove("is-open");
    this.scrimTarget.hidden = true;
    document.body.style.removeProperty("overflow");
    this.setExpanded(false);
  }

  get isOpen() {
    return this.hasPanelTarget && this.panelTarget.classList.contains("is-open");
  }

  setExpanded(open) {
    this.element
      .querySelector(".nav-toggle")
      ?.setAttribute("aria-expanded", open ? "true" : "false");
  }
}
